import { CFDEventType, SaleUpdatedData } from '@lightspeed/cfd-event-types'
import { PAYMENT_TYPES } from '@vend/business-components'
import { useChannel } from 'ably/react'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Payment } from '../../../models/payment'
import { SaleStatus, SaleSyncStatus } from '../../../models/sale.constants'
import {
  useIsCalculatingSale,
  useRegisterManager,
} from '../../../ng-compat/hooks'
import {
  createNoPaymentSaleCompletedPayload,
  createSaleHiddenPayload,
  createSalePaymentReceivedOrCompletedPayload,
  createSaleUpdatedPayload,
} from '../../../state/selectors/cfdEventPayloadSelectors'
import {
  ReduxCurrentSale,
  selectCurrentSale,
  selectSalePayments,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { RootState } from '../../../state/store'

const STALE_PAYMENT_THRESHOLD_MS = 5000

type SaleChangeEventsProps = {
  channelName: string
}

export const SaleChangeEvents: React.FunctionComponent<SaleChangeEventsProps> = ({
  channelName,
}) => {
  const [isOnRootPage, setIsOnRootPage] = useState(
    isLocationPathnameRoot(window.location.pathname)
  )
  const [isCashDiscountActive, setIsCashDiscountActive] = useState(false)

  const { channel, publish } = useChannel(channelName)
  const registerManager = useRegisterManager()
  const isCalculatingSale = useIsCalculatingSale()

  const currentSale = useSelector(selectCurrentSale)
  const payments = useSelector(selectSalePayments)
  const salePayload = useSelector((state: RootState) =>
    createSaleUpdatedPayload(state, registerManager, isCashDiscountActive)
  )

  const prevStatusUpdatedAt = useRef<string | undefined>(undefined)
  const prevPayments = useRef<Payment[]>([])
  const prevIsCalculatingSale = useRef<boolean>(isCalculatingSale)
  const prevSalePayload = useRef(salePayload.data)
  const prevSaleLocalId = useRef(currentSale.localId)
  // Raised once the sale is completed and lowered when
  // a new sale begins or line items are cleared. While raised, SALE_UPDATED
  // events are suppressed so the terminal's completed / approved screen is
  // never reverted back to the sale items before the next customer starts.
  const hasPublishedSaleCompleted = useRef(false)

  const paymentsSum = isCalculatingSale ? undefined : getSumOfPayments(payments)
  const saleTotal = isCalculatingSale ? undefined : getSaleTotal(currentSale)
  const isSaleFullyPaid =
    paymentsSum !== undefined &&
    saleTotal !== undefined &&
    Math.abs(paymentsSum) >= Math.abs(saleTotal)

  // Notifies Ably presence on mount.
  useEffect(() => {
    channel.presence.enter()
    return () => {
      channel.presence.leave()
    }
  }, [channel])

  // Reacts to in-app navigation events.
  useEffect(() => {
    const handleInAppNavigation = (event: any) => {
      const to = event.detail.to
      const from = event.detail.from

      setIsOnRootPage(isNavigationPathnameRoot(to))

      // Handles navigation away from the root page within the web register.
      if (from === '/' && to && to !== '/') {
        publish(CFDEventType.SALE_HIDDEN, createSaleHiddenPayload())
      }

      // Handles navigation back to the root page within the web register.
      if (to && to === '/' && from && from !== '/confirmation') {
        publish(CFDEventType.SALE_UPDATED, salePayload)
      }
    }

    window.addEventListener('angular-navigation-start', handleInAppNavigation)

    return () => {
      window.removeEventListener(
        'angular-navigation-start',
        handleInAppNavigation
      )
    }
  }, [publish, setIsOnRootPage, salePayload])

  // Reacts to cash discount selection/deselection events dispatched by the
  // payment flow. When cash with a discount is selected, the next SALE_UPDATED
  // will include cashDiscount data and an adjusted total. Deselection (modal
  // dismissed without paying) reverts to the standard payload.
  useEffect(() => {
    const handleSelected = () => setIsCashDiscountActive(true)
    const handleDeselected = () => setIsCashDiscountActive(false)

    window.addEventListener('cfd-cash-payment-selected', handleSelected)
    window.addEventListener('cfd-cash-payment-dismissed', handleDeselected)

    return () => {
      window.removeEventListener('cfd-cash-payment-selected', handleSelected)
      window.removeEventListener('cfd-cash-payment-dismissed', handleDeselected)
    }
  }, [])

  // Publishes a SALE_UPDATED event when the cash discount active state changes,
  // so the CFD immediately reflects the discount being applied or removed.
  const prevIsCashDiscountActive = useRef(isCashDiscountActive)
  useEffect(() => {
    if (prevIsCashDiscountActive.current !== isCashDiscountActive) {
      publish(CFDEventType.SALE_UPDATED, salePayload)
      // Update the ref so the subsequent "sale data changes" effect does not
      // treat the cashDiscount difference as a new change and publish again.
      prevSalePayload.current = salePayload.data
    }
    prevIsCashDiscountActive.current = isCashDiscountActive
  }, [isCashDiscountActive, publish, salePayload])

  // Reacts to sale line items, discount, fulfillment, and account payment changes.
  useEffect(() => {
    const {
      hasLineItemsCleared,
      hasNewFulfillment,
      hasNewDiscount,
      hasNewCashDiscount,
      hasNewAccountPayment,
      hasNewTip,
      hasNewFees,
    } = getSaleDataChanges(prevSalePayload.current, salePayload.data)

    const isNewSale = currentSale.localId !== prevSaleLocalId.current
    const shouldResetCashDiscount =
      (isNewSale || hasLineItemsCleared) && isCashDiscountActive
    if (isNewSale || hasLineItemsCleared) {
      hasPublishedSaleCompleted.current = false
      setIsCashDiscountActive(false)
    }

    const hasSaleDataChanged =
      hasLineItemsCleared ||
      hasNewFulfillment ||
      hasNewDiscount ||
      hasNewCashDiscount ||
      hasNewAccountPayment ||
      ((hasNewTip || hasNewFees) && !isCalculatingSale) ||
      (prevIsCalculatingSale.current && !isCalculatingSale)

    if (
      isOnRootPage &&
      hasSaleDataChanged &&
      !hasPublishedSaleCompleted.current &&
      !shouldResetCashDiscount
    ) {
      publish(CFDEventType.SALE_UPDATED, salePayload)
    }

    prevIsCalculatingSale.current = isCalculatingSale
    prevSalePayload.current = salePayload.data
    prevSaleLocalId.current = currentSale.localId
  }, [
    salePayload,
    publish,
    isOnRootPage,
    isCalculatingSale,
    currentSale.localId,
    isCashDiscountActive,
  ])

  // Reacts to sale status changes.
  useEffect(() => {
    const hasStatusChanged =
      currentSale.statusUpdatedAt !== prevStatusUpdatedAt.current &&
      !!currentSale.status &&
      currentSale.syncStatus === SaleSyncStatus.MODIFIED
    const hasPayments = payments.length > 0
    const isSpecialStatus =
      currentSale.status &&
      [SaleStatus.ACCOUNT_SALE, SaleStatus.LAYBY_SALE].includes(
        currentSale.status
      )

    if (!hasStatusChanged) {
      return
    }

    if (isSpecialStatus) {
      hasPublishedSaleCompleted.current = true

      createNoPaymentSaleCompletedPayload(
        registerManager,
        currentSale.status!
      ).then(saleCompletedPayload => {
        publish(CFDEventType.SALE_COMPLETED, saleCompletedPayload)
      })
      return
    }

    if (!isSaleFullyPaid || !hasPayments) {
      return
    }

    hasPublishedSaleCompleted.current = true

    createSalePaymentReceivedOrCompletedPayload(
      currentSale,
      payments[payments.length - 1],
      registerManager,
      CFDEventType.SALE_COMPLETED
    ).then(saleCompletedPayload => {
      publish(CFDEventType.SALE_COMPLETED, saleCompletedPayload)
    })
  }, [currentSale, payments, publish, registerManager, isSaleFullyPaid])

  // Reacts to new payments being added to the sale.
  useEffect(() => {
    if (isCalculatingSale || isSaleFullyPaid) {
      return
    }

    const newPayments = getNewPayments(prevPayments.current, payments)

    newPayments.forEach(payment => {
      if (
        payment.paymentType.type_id === PAYMENT_TYPES.LOYALTY ||
        payment.paymentType.type_id === PAYMENT_TYPES.STORE_CREDIT
      ) {
        publish(CFDEventType.SALE_UPDATED, salePayload)
      } else {
        createSalePaymentReceivedOrCompletedPayload(
          currentSale,
          payment,
          registerManager,
          CFDEventType.SALE_PAYMENT_RECEIVED
        ).then(paymentReceivedPayload => {
          publish(CFDEventType.SALE_PAYMENT_RECEIVED, paymentReceivedPayload)
          publish(CFDEventType.SALE_UPDATED, salePayload)
        })
      }
    })

    prevPayments.current = payments
  }, [
    currentSale,
    payments,
    publish,
    registerManager,
    salePayload,
    isSaleFullyPaid,
    isCalculatingSale,
  ])

  useEffect(() => {
    prevStatusUpdatedAt.current = currentSale.statusUpdatedAt ?? undefined
  }, [currentSale.statusUpdatedAt])

  return null
}

const getNewPayments = (prevPayments: Payment[], newPayments: Payment[]) => {
  return newPayments.filter(
    p =>
      prevPayments.findIndex(prevP => prevP.localId === p.localId) === -1 &&
      // Ignores stale payments.
      Date.now() - new Date(p.paymentDate).getTime() <
        STALE_PAYMENT_THRESHOLD_MS
  )
}

const getSaleDataChanges = (
  prevSaleData: SaleUpdatedData,
  newSaleData: SaleUpdatedData
) => {
  return {
    hasNewAccountPayment:
      prevSaleData.accountPayment !== newSaleData.accountPayment,
    hasNewDiscount:
      prevSaleData.discount?.amount !== newSaleData.discount?.amount ||
      prevSaleData.discount?.percentage !== newSaleData.discount?.percentage,
    hasNewCashDiscount:
      prevSaleData.cashDiscount?.amount !== newSaleData.cashDiscount?.amount ||
      prevSaleData.cashDiscount?.percentage !==
        newSaleData.cashDiscount?.percentage,
    hasNewFulfillment: newSaleData.lineItems.some(
      item =>
        item.fulfillmentType !==
        prevSaleData.lineItems.find(prevItem => prevItem.id === item.id)
          ?.fulfillmentType
    ),
    hasNewTip:
      prevSaleData.tip?.amount !== newSaleData.tip?.amount ||
      prevSaleData.tip?.afterTax !== newSaleData.tip?.afterTax,
    hasNewFees: prevSaleData.fees !== newSaleData.fees,
    hasLineItemsCleared:
      prevSaleData.lineItems.length > 0 && newSaleData.lineItems.length === 0,
  }
}

const isNavigationPathnameRoot = (pathname: string) =>
  pathname === '/' || pathname === undefined || pathname === '/confirmation'

const isLocationPathnameRoot = (pathname: string) =>
  pathname === '/webregister' ||
  pathname === '/webregister/' ||
  pathname === '/webregister/confirmation'

const getSumOfPayments = (payments: Payment[]) =>
  payments.reduce((total, payment) => total + Number(payment.amount), 0)

const getSaleTotal = (currentSale: ReduxCurrentSale) =>
  Number(currentSale.finals.inclusivePrice) +
  Number(currentSale.payAccountBalance?.amount || 0)
